import { ArrowRight } from "lucide-react";
import { Seo, Eyebrow, CTAButton, Stat } from "@/components/site/primitives";
import { Reveal, Stagger, Item } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { MembraneDiagram } from "@/components/site/Diagrams";

const PRINCIPLES = [
  { t: "Sovereign by construction", d: "Embeddings are computed locally, vectors live on-disk, metadata stays in local storage. No byte of your corpus is required to leave the device — not as a setting, as an architecture." },
  { t: "The attack surface you remove", d: "A corpus that never traverses the network cannot be intercepted on it. A store that lives on your machine cannot be exfiltrated from a vendor breach — because the vendor never holds it." },
  { t: "Consent-gated capture", d: "Ambient capture routes through an approval inbox. Sources are explicitly enabled; nothing enters durable memory without consent." },
  { t: "Graceful degradation", d: "No GPU, no API key, no internet? Retrieval still answers and a local model keeps reasoning. Offline is a first-class mode, never a failure state." },
];

export default function Security() {
  return (
    <div data-testid="security-page">
      <Seo title="Smritium — Security" description="Local-first as a security model, not a feature. What never leaves cannot leak." />
      <PageHero
        eyebrow="Smritium · Security"
        title="What never leaves cannot leak."
        lead="Most privacy is a policy you are asked to trust. Local-first is a property you can verify. Keeping the corpus on-device is a security architecture, not a feature toggle."
      />

      <Section>
        <Stagger className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
          {PRINCIPLES.map((p) => (
            <Item key={p.t} className="bg-ink p-8 md:p-10">
              <h3 className="font-serif text-xl text-bone">{p.t}</h3>
              <p className="mt-4 text-[15px] leading-relaxed text-mist">{p.d}</p>
            </Item>
          ))}
        </Stagger>
      </Section>

      <Section className="border-t border-white/10">
        <div className="grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Governed egress</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
              When something must cross, the Membrane makes it explicit.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-mist">
              The Membrane detects and redacts PII, shows exactly what is about to leave, requires human approval, and
              writes a tamper-evident egress audit log. Every byte that ever left is provable.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="border border-white/10 bg-white/[0.015] p-8">
              <MembraneDiagram className="text-bone/70" />
              <p className="mt-4 font-mono text-xs text-mist">Fig. 1 — The Smritium Membrane.</p>
            </div>
          </Reveal>
        </div>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal>
          <div className="grid grid-cols-2 gap-8 border-y border-white/10 py-12 md:grid-cols-4">
            <Stat value="0" label="bytes required to leave the device to answer" />
            <Stat value="100%" label="of local operations run without a cloud call" />
            <Stat value="Append-only" label="tamper-evident egress audit log" />
            <Stat value="MIT" label="open-source core — verify, don't trust" />
          </div>
        </Reveal>
      </Section>

      <Section className="border-t border-white/10">
        <Reveal className="flex flex-col items-start justify-between gap-8 md:flex-row md:items-center">
          <h2 className="max-w-xl text-balance font-serif text-3xl leading-tight tracking-tighter text-bone md:text-4xl">
            Found something? We run a responsible-disclosure program.
          </h2>
          <CTAButton to="/legal/security-disclosure" variant="outline">
            Security disclosure <ArrowRight className="h-4 w-4" />
          </CTAButton>
        </Reveal>
      </Section>
    </div>
  );
}
